<template>
    <div class="main-content">
        <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 1060">
            <transition-group name="slide-fade">
                <div v-for="alert in alerts" :key="alert.id"
                    class="toast show align-items-center border-0 shadow-lg mb-2"
                    :class="'bg-' + alert.type + '-subtle text-' + alert.type" role="alert">
                    <div class="d-flex p-3">
                        <i :class="alert.icon" class="me-3 fs-5"></i>
                        <div class="toast-body p-0 fw-medium">
                            {{ alert.message }}
                        </div>
                        <button type="button" class="btn-close ms-auto" @click="removeAlert(alert.id)"></button>
                    </div>
                </div>
            </transition-group>
        </div>

        <header class="content-header mb-2 mt-n5">
            <nav aria-label="breadcrumb">
                <ol class="breadcrumb mb-0">
                    <li class="breadcrumb-item"><router-link class="nav-link active" to="/">Início</router-link></li>
                    <li class="breadcrumb-item active text-dark small">Templates de E-mail</li>
                </ol>
            </nav>
        </header>

        <div class="card p-4 shadow-subtle rounded-4 border-0">
            <div class="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h4 class="mb-0 fw-bold">Régua de Relacionamento</h4>
                    <span class="text-muted fs-7">Exibindo {{ templates.length }} de {{ totalItems }} templates</span>
                </div>

                <div class="d-flex gap-2 align-items-center">
                    <transition name="fade">
                        <div v-if="selectedIds.length > 0" class="d-flex align-items-center me-2 pe-3 border-end">
                            <button class="btn btn-pmg-danger btn-sm px-3"
                                @click="confirmDelete(selectedIds.length, true)">
                                <i class="bi bi-trash3 me-1"></i> Excluir ({{ selectedIds.length }})
                            </button>
                        </div>
                    </transition>

                    <button class="btn btn-pmg-light" @click="showFilter = !showFilter"
                        :class="{ 'active': showFilter }">
                        <i class="bi bi-funnel"></i> Filtrar
                    </button>

                    <button class="btn btn-pmg-primary" @click="openModal('create')">
                        <i class="bi bi-plus-lg me-1"></i> Novo Template
                    </button>
                </div>
            </div>

            <div class="collapse" :class="{ 'show': showFilter }">
                <div class="card card-body mb-4 border-0 bg-light rounded-3">
                    <div class="row g-3">
                        <div class="col-md-10">
                            <select v-model="filter.event" class="form-select form-control-pmg">
                                <option value="">Todos os eventos</option>
                                <option v-for="evt in availableEvents" :key="evt.value" :value="evt.value">
                                    {{ evt.label }}
                                </option>
                            </select>
                        </div>
                        <div class="col-md-2 d-flex">
                            <button class="btn btn-dark w-100 btn-pmg-dark fw-bold" @click="applyFilter">
                                <i class="bi bi-search me-1"></i> Filtrar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="table-responsive" style="overflow: visible;">
                <table class="table table-pmg align-middle">
                    <thead>
                        <tr>
                            <th style="width: 5%">
                                <input type="checkbox" class="form-check-input" @change="toggleAll"
                                    :checked="isAllSelected">
                            </th>
                            <th style="width: 25%">Evento (Gatilho)</th>
                            <th style="width: 40%">Assunto do E-mail</th>
                            <th style="width: 15%">Status</th>
                            <th style="width: 15%" class="text-end">Ações</th>
                        </tr>
                    </thead>

                    <tbody v-if="templates.length > 0">
                        <tr v-for="item in templates" :key="item.id"
                            :class="{ 'selected-row': selectedIds.includes(item.id) }">
                            <td><input type="checkbox" class="form-check-input" :value="item.id" v-model="selectedIds">
                            </td>
                            <td>
                                <span class="fw-bold text-dark">
                                    {{ getEventLabel(item.event) }}
                                </span>
                            </td>
                            <td>
                                <div class="pmg-desc-cell text-truncate-custom fw-medium" :title="item.subject">
                                    {{ getReadableSubject(item.subject) }}
                                </div>
                            </td>
                            <td>
                                <span v-if="item.is_active"
                                    class="badge bg-success-subtle text-success border border-success-subtle px-3 py-2 rounded-pill">Ativo</span>
                                <span v-else
                                    class="badge bg-secondary-subtle text-secondary border px-3 py-2 rounded-pill">Inativo</span>
                            </td>
                            <td class="text-end" style="overflow: visible;">
                                <div class="dropdown">
                                    <button class="btn btn-pmg-icon border" data-bs-toggle="dropdown"
                                        data-bs-boundary="viewport">
                                        <i class="bi bi-three-dots-vertical"></i>
                                    </button>
                                    <ul class="dropdown-menu dropdown-menu-end shadow border-0" style="z-index: 1070;">
                                        <li><a class="dropdown-item" href="#"
                                                @click.prevent="openModal('edit', item)"><i
                                                    class="bi bi-pencil me-2"></i>Editar Template</a></li>
                                        <li>
                                            <hr class="dropdown-divider">
                                        </li>
                                        <li><a class="dropdown-item text-danger" href="#"
                                                @click.prevent="confirmDelete(item)"><i
                                                    class="bi bi-trash me-2"></i>Excluir</a></li>
                                    </ul>
                                </div>
                            </td>
                        </tr>
                    </tbody>

                    <tbody v-else>
                        <tr>
                            <td colspan="5" class="text-center py-5 text-muted">
                                <div class="d-flex flex-column align-items-center justify-content-center">
                                    <i class="bi bi-envelope-paper fs-1 mb-2 text-secondary opacity-50"></i>
                                    <span class="fw-medium">Nenhum template configurado.</span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="text-center mt-4" v-if="hasMore">
                <button
                    class="btn btn-pmg-light px-5 btn-sm fw-bold d-inline-flex align-items-center justify-content-center"
                    @click="loadMore" :disabled="loading" style="min-width: 220px;">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                        aria-hidden="true"></span>
                    <span>{{ loading ? 'Carregando dados...' : 'Carregar mais itens' }}</span>
                </button>
            </div>
        </div>

        <div class="modal fade" id="modalTemplate" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4">
                    <div class="modal-header border-bottom-0 pt-4 px-4 pb-0">
                        <h5 class="fw-bold m-0">{{ isEditing ? 'Editar Template de E-mail' : 'Novo Template de E-mail'
                        }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" id="closeModalBtn"></button>
                    </div>

                    <div class="modal-body p-4 pt-3">
                        <div class="row g-4">
                            <div class="col-lg-8 border-end pe-lg-4">

                                <div class="row mb-4">
                                    <div class="col-md-8">
                                        <label class="form-label small fw-bold text-muted">Evento (Gatilho do
                                            Sistema)</label>
                                        <select v-model="form.event" class="form-select form-control-pmg"
                                            :disabled="isEditing">
                                            <option value="" disabled>Selecione quando este e-mail será enviado...
                                            </option>
                                            <option v-for="evt in filteredAvailableEvents" :key="evt.value" :value="evt.value">
                                                {{ evt.label }}
                                            </option>
                                        </select>
                                        <div v-if="!isEditing && filteredAvailableEvents.length === 0" class="text-danger small mt-1">
                                            Todos os eventos disponíveis já possuem um template cadastrado.
                                        </div>
                                    </div>
                                    <div class="col-md-4 d-flex align-items-end">
                                        <div class="form-check form-switch mb-2">
                                            <input class="form-check-input" type="checkbox" role="switch"
                                                id="activeSwitch" v-model="form.is_active">
                                            <label class="form-check-label fw-medium ms-1" for="activeSwitch">Template
                                                Ativo</label>
                                        </div>
                                    </div>
                                </div>

                                <ul class="nav nav-tabs pmg-tabs mb-3" role="tablist">
                                    <li class="nav-item" role="presentation">
                                        <button class="nav-link active fw-bold" data-bs-toggle="tab"
                                            data-bs-target="#tab-editor" type="button" role="tab">Conteúdo do
                                            E-mail</button>
                                    </li>
                                    <li class="nav-item" role="presentation">
                                        <button class="nav-link fw-bold" data-bs-toggle="tab"
                                            data-bs-target="#tab-preview" type="button" role="tab"
                                            @click="fetchPreview">Visualizar (Preview)</button>
                                    </li>
                                </ul>

                                <div class="tab-content">
                                    <div class="tab-pane fade show active" id="tab-editor" role="tabpanel">

                                        <div class="mb-4 pb-4 border-bottom">
                                            <label class="form-label small fw-bold text-muted mb-3">Escolha o Estilo
                                                (Design) do E-mail</label>
                                            <div class="row g-3">
                                                <div class="col-md-3" v-for="layout in availableLayouts"
                                                    :key="layout.id">
                                                    <div class="layout-card border rounded-3 p-3 text-center h-100"
                                                        :class="{ 'border-primary bg-primary-subtle shadow-sm': form.layout_id === layout.id }"
                                                        @click="form.layout_id = layout.id"
                                                        style="cursor: pointer; transition: all 0.2s;">
                                                        <i class="fs-2 d-block mb-2"
                                                            :class="[layout.icon, form.layout_id === layout.id ? 'text-primary' : 'text-muted']"></i>
                                                        <h6 class="fw-bold mb-1"
                                                            :class="{ 'text-primary': form.layout_id === layout.id }">{{
                                                                layout.name }}</h6>
                                                        <span class="small text-muted" style="font-size: 0.75rem;">{{
                                                            layout.desc }}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div class="mb-3">
                                            <label class="form-label small fw-bold text-muted">Assunto do E-mail
                                                (Exibido na caixa de entrada)</label>
                                            <input v-model="form.subject" ref="subjectInput"
                                                @focus="lastFocusedField = 'subject'" type="text"
                                                class="form-control form-control-pmg"
                                                placeholder="ex: Nova fatura gerada para {{ customer_name }}">

                                            <div v-if="previewData.subject" class="mt-2 text-muted small">
                                                <i class="bi bi-eye me-1"></i>
                                                Exemplo: <span class="fw-medium text-dark italic-text">"{{
                                                    previewData.subject }}"</span>
                                            </div>
                                        </div>

                                        <div class="mb-3">
                                            <label class="form-label small fw-bold text-muted">Título Principal (Exibido
                                                dentro do corpo do e-mail)</label>
                                            <input v-model="form.internal_title" ref="internalTitleInput"
                                                @focus="lastFocusedField = 'internal_title'" type="text"
                                                class="form-control form-control-pmg"
                                                placeholder="ex: Olá, {{ customer_name }}! Temos novidades.">
                                        </div>

                                        <div class="mb-3">
                                            <label
                                                class="form-label small fw-bold text-muted d-flex justify-content-between">
                                                <span>Corpo da Mensagem</span>
                                            </label>

                                            <div class="form-control form-control-pmg bg-light p-0"
                                                @click="lastFocusedField = 'body_text'" style="overflow: hidden;">

                                                <QuillEditor ref="quillEditor" v-model:content="form.body_text"
                                                    contentType="html" theme="snow" :toolbar="editorOptions.toolbar"
                                                    placeholder="Digite aqui a mensagem principal do e-mail..."
                                                    style="min-height: 250px; border: none; font-size: 14px;"
                                                    @focus="lastFocusedField = 'body_text'" />
                                            </div>
                                        </div>
                                    </div>

                                    <div class="tab-pane fade" id="tab-preview" role="tabpanel">
                                        <div v-if="loadingPreview" class="text-center py-5">
                                            <div class="spinner-border text-primary" role="status"></div>
                                            <p class="mt-2 text-muted small">Gerando preview com o layout selecionado...
                                            </p>
                                        </div>
                                        <div v-else class="preview-container border rounded-3 p-3 bg-light">
                                            <div class="mb-3 pb-2 border-bottom">
                                                <span class="text-muted small fw-bold me-2">Assunto Final:</span>
                                                <span class="fw-bold">{{ previewData.subject || 'Preencha o assunto para ver o exemplo' }}</span>
                                            </div>
                                            <iframe :srcdoc="previewData.html_preview" class="w-100 border-0 rounded"
                                                style="min-height: 400px; background: #fff;"></iframe>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="col-lg-4">
                                <div class="card border-0 bg-primary-subtle rounded-4 p-3 h-100 shadow-none"
                                    style="background-color: #f0f4f8;">
                                    <div class="d-flex align-items-center mb-3">
                                        <i class="bi bi-braces text-primary fs-3 me-2"></i>
                                        <h6 class="fw-bold m-0 text-dark">Variáveis Dinâmicas</h6>
                                    </div>

                                    <p class="small text-muted mb-4">
                                        Clique em uma variável abaixo para inseri-la automaticamente no campo que você
                                        está editando no momento.
                                    </p>

                                    <div class="variables-list d-flex flex-column gap-2 overflow-auto"
                                        style="max-height: 500px;">
                                        <button v-for="variable in filteredVariables" :key="variable.key"
                                            @click="insertVariable(variable.key)"
                                            class="btn btn-sm btn-light border variable-pill text-start w-100 d-flex justify-content-between align-items-center p-2">

                                            <div class="d-flex flex-column text-start">
                                                <span class="fw-bold text-dark" style="font-size: 0.85rem;">{{
                                                    variable.label }}</span>
                                                <span class="font-monospace text-muted mt-1" style="font-size: 0.75rem;"
                                                    v-text="'{{ ' + variable.key + ' }}'"></span>
                                            </div>

                                            <i class="bi bi-cursor text-primary opacity-50 ms-2"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer border-top px-4 py-3 bg-light rounded-bottom-4">
                        <button class="btn btn-pmg-light me-2" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-pmg-primary px-4" :disabled="!isFormValid || loading"
                            @click="saveTemplate">
                            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                            {{ isEditing ? 'Atualizar Template' : 'Salvar Template' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal fade" id="confirmDeleteModal" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-sm modal-dialog-centered">
                <div class="modal-content border-0 shadow-lg rounded-4 text-center p-3">
                    <div class="modal-body">
                        <div class="text-danger mb-3">
                            <i class="bi bi-exclamation-triangle-fill fs-1"></i>
                        </div>
                        <h5 class="fw-bold">Tem certeza?</h5>
                        <p class="text-muted small">{{ isBulkDelete ? `Excluir ${selectedIds.length} templates
                            selecionados?` : `Excluir o template do evento "${getEventLabel(itemToDelete?.event)}"?` }}
                        </p>
                    </div>
                    <div class="d-flex gap-2 justify-content-center pb-2">
                        <button class="btn btn-pmg-light px-4" data-bs-dismiss="modal">Cancelar</button>
                        <button class="btn btn-danger px-4 rounded-3" @click="executeDeletion">Confirmar</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '@/services/api';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
    components: {
        QuillEditor
    },
    data() {
        return {
            editorOptions: {
                toolbar: [
                    ['bold', 'italic', 'underline', 'strike'],        // botões de formatação
                    [{ 'list': 'ordered' }, { 'list': 'bullet' }],     // listas
                    [{ 'align': [] }],                                // alinhamento
                    ['link'],                                         // links
                    ['clean']                                         // remover formatação
                ]
            },
            templates: [],
            alerts: [],
            isEditing: false,

            form: {
                id: null,
                event: '',
                layout_id: 'padrao',
                subject: '',
                internal_title: '',
                body_text: '',
                is_active: true
            },

            lastFocusedField: 'body_text',

            selectedIds: [],
            filter: { event: '' },

            availableEvents: [
                { value: 'code_portal_client', label: 'Código de acesso Portal do Cliente' },
                { value: 'new_revenue', label: 'Nova Receita / Fatura Gerada' },
                { value: 'payment_overdue', label: 'Cobrança em Atraso' },
                { value: 'payment_received', label: 'Confirmação de Pagamento' },
                { value: 'reset_password_user', label: 'Resetar senha de acesso' },
                { value: 'new_protocol_crm', label: 'Protocolo de atendimento CRM' },
                { value: 'welcome_client', label: 'Novo cliente registrado' },
                { value: 'code_portal_doacao', label: 'Código de acesso Portal de Doação' },
            ],

            availableLayouts: [
                { id: 'padrao', name: 'Padrão', icon: 'bi-layout-text-window', desc: 'Simples, focado no texto e leitura clara.' },
                { id: 'moderno', name: 'Moderno', icon: 'bi-window-sidebar', desc: 'Arredondado com destaques visuais leves.' },
                { id: 'classico', name: 'Clássico', icon: 'bi-envelope-paper', desc: 'Formal, estilo papel timbrado tradicional.' },
                { id: 'tecnologico', name: 'Tecnológico', icon: 'bi-cpu', desc: 'Dark mode elegante, ideal para SaaS/Tech.' }
            ],

            allVariables: [
                { key: 'tenant_name', label: 'Nome da sua Empresa', contexts: ['all'] },
                { key: 'customer_name', label: 'Nome do Cliente', contexts: ['new_revenue', 'payment_overdue', 'payment_received','new_protocol_crm','code_portal_client','code_portal_doacao'] },
                { key: 'document_number', label: 'Documento (CPF/CNPJ)', contexts: ['new_revenue', 'payment_overdue', 'payment_received'] },
                { key: 'transaction_description', label: 'Descrição da Cobrança', contexts: ['new_revenue', 'payment_overdue', 'payment_received'] },
                { key: 'transaction_amount', label: 'Valor da Cobrança (R$)', contexts: ['new_revenue', 'payment_overdue', 'payment_received'] },
                { key: 'transaction_due_date', label: 'Data de Vencimento', contexts: ['new_revenue', 'payment_overdue', 'payment_received'] },
                { key: 'transaction_id', label: 'Código da Transação', contexts: ['new_revenue', 'payment_overdue', 'payment_received'] },
                { key: 'transaction_details_html', label: 'Caixa de Detalhes da Cobrança', contexts: ['new_revenue', 'payment_overdue', 'payment_received'] },
                { key: 'payment_section_html', label: 'Bloco de Pagamento (Automático)', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'pix_section_html', label: 'Bloco Exclusivo: PIX Completo', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'boleto_section_html', label: 'Bloco Exclusivo: Boleto Completo', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'checkout_url', label: 'Link de Pagamento (Fatura Online)', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'pix_qr_code', label: 'Imagem do QR Code (PIX)', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'pix_key', label: 'Chave PIX (Apenas o texto)', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'boleto_pdf_url', label: 'Link do PDF (Boleto)', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'boleto_line', label: 'Linha Digitável', contexts: ['new_revenue', 'payment_overdue'] },
                { key: 'tenant_name', label: 'Código de acesso', contexts: ['code_portal_client'] },
                { key: 'user_name', label: 'Nome do usuário', contexts: ['reset_password_user'] },
                { key: 'password_reset', label: 'Senha redefinida', contexts: ['reset_password_user'] },
                { key: 'protocol', label: 'Protocolo de atendimento', contexts: ['new_protocol_crm'] },
                { key: 'reason_crm', label: 'Motivo / Assunto', contexts: ['new_protocol_crm'] },
                { key: 'description_crm', label: 'Descrição atendimento', contexts: ['new_protocol_crm'] },
                { key: 'customer_document', label: 'Documento do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_phone', label: 'Telefone do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_email', label: 'E-mail do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_zipcode', label: 'CEP endereço do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_street', label: 'Endereço do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_neighborhood', label: 'Bairro do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_city', label: 'Cidade do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_state', label: 'Estado(UF) do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_education_level', label: 'Nível educacional do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_occupation', label: 'Ocupação do Cliente', contexts: ['welcome_client'] },
                { key: 'customer_credit_limit', label: 'Limite de crédito do Cliente', contexts: ['welcome_client'] },
                { key: 'code', label: 'Código enviado', contexts: ['code_portal_client','code_portal_doacao'] },

            ],

            previewData: { subject: '', html_preview: '' },
            loadingPreview: false,

            totalItems: 0,
            currentPage: 1,
            lastPage: 1,
            loading: false,
            hasMore: false,
            showFilter: false,
            modalInstance: null,
            itemToDelete: null,
            isBulkDelete: false
        }
    },
    computed: {
        isAllSelected() { return this.templates.length > 0 && this.templates.every(i => this.selectedIds.includes(i.id)); },
        isFormValid() { return this.form.event && this.form.subject.trim() && this.form.body_text.trim(); },
        filteredVariables() {
            const currentEvent = this.form.event;
            if (!currentEvent) {
                return this.allVariables.filter(variable => variable.contexts.includes('all'));
            }
            return this.allVariables.filter(variable => {
                return variable.contexts.includes('all') || variable.contexts.includes(currentEvent);
            });
        },
        filteredAvailableEvents() {
            const savedEvents = this.templates.map(t => t.event);
            return this.availableEvents.filter(evt => {
                if (this.isEditing && this.form.event === evt.value) {
                    return true;
                }
                return !savedEvents.includes(evt.value);
            });
        }
    },
    mounted() {
        const modalElement = document.getElementById('modalTemplate');
        if (modalElement) {
            this.modalInstance = new window.bootstrap.Modal(modalElement);
        }
        this.fetchTemplates();
    },
    methods: {
        getEventLabel(eventValue) {
            const event = this.availableEvents.find(e => e.value === eventValue);
            return event ? event.label : eventValue;
        },

        getReadableSubject(subject) {
            if (!subject) return '';
            const mockData = {
                'tenant_name': 'Nome da sua empresa',
                'customer_name': 'Cliente Exemplo',
                'transaction_amount': '150,00',
                'transaction_id': 'TX-1234',
                'transaction_description': 'Atraso de cobrança'
            };
            let readable = subject;
            for (const [key, value] of Object.entries(mockData)) {
                const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
                readable = readable.replace(regex, value);
            }
            return readable;
        },

        async insertVariable(variableKey) {
            const textToInsert = `{{ ${variableKey} }}`;
            try {
                await navigator.clipboard.writeText(textToInsert);
                this.showAlert(`Variável ${textToInsert} inserida e copiada!`, 'success');
            } catch (err) { console.log(err) }

            const fieldName = this.lastFocusedField;
            if (fieldName === 'body_text') {
                const quillComponent = this.$refs.quillEditor;
                if (!quillComponent) return;

                const quill = quillComponent.getQuill();
                const range = quill.getSelection(true);
                quill.insertText(range.index, textToInsert);
                quill.setSelection(range.index + textToInsert.length);

                return;
            }

            let refName = fieldName === 'subject' ? 'subjectInput' : 'internalTitleInput';
            const element = this.$refs[refName];
            if (!element) return;
            const start = element.selectionStart;
            const end = element.selectionEnd;
            const originalScrollTop = element.scrollTop;

            const originalText = element.value || '';
            const newText = originalText.substring(0, start) + textToInsert + originalText.substring(end);

            element.value = newText;
            this.form[fieldName] = newText;

            const newCursorPos = start + textToInsert.length;
            element.focus();
            element.setSelectionRange(newCursorPos, newCursorPos);
            element.scrollTop = originalScrollTop;
        },

        async fetchTemplates(page = 1, append = false) {
            this.loading = true;
            try {
                const params = { page: page, search: this.filter.event };
                const response = await api.get('/email-templates', { params });
                const payload = response.data;
                const items = Array.isArray(payload) ? payload : (payload.data || []);
                const paginationData = payload.meta || payload;
                const { current_page, last_page, total } = paginationData;

                if (append) {
                    this.templates = [...this.templates, ...items];
                } else {
                    this.templates = items;
                }

                this.currentPage = current_page || 1;
                this.lastPage = last_page || 1;
                this.totalItems = total || items.length;
                this.hasMore = this.currentPage < this.lastPage;

            } catch (error) {
                this.showAlert('Falha ao comunicar com o servidor.', 'error');
            } finally {
                this.loading = false;
            }
        },

        loadMore() {
            if (this.hasMore && !this.loading) this.fetchTemplates(this.currentPage + 1, true);
        },

        applyFilter() {
            this.showFilter = false;
            this.selectedIds = [];
            this.fetchTemplates(1, false);
        },

        showAlert(message, type = 'success') {
            const id = Date.now();
            let icon = 'bi-check-circle-fill';
            if (type === 'danger') icon = 'bi-exclamation-circle-fill';
            if (type === 'warning') icon = 'bi-exclamation-triangle-fill';
            if (type === 'error') icon = 'bi-x-circle-fill';

            this.alerts.push({ id, message, type, icon });
            setTimeout(() => this.removeAlert(id), type === 'error' ? 6000 : 4000);
        },

        removeAlert(id) {
            this.alerts = this.alerts.filter(a => a.id !== id);
        },

        async fetchPreview() {
            if (!this.form.body_text) return;

            this.loadingPreview = true;
            try {
                const response = await api.post('/email-templates/preview', {
                    layout_id: this.form.layout_id,
                    subject: this.form.subject,
                    internal_title: this.form.internal_title,
                    body_text: this.form.body_text
                });
                this.previewData = response.data;
            } catch (error) {
                this.showAlert('Erro ao carregar o preview do layout.', 'error');
            } finally {
                this.loadingPreview = false;
            }
        },

        openModal(mode, item = null) {
            if (mode === 'edit') {
                this.isEditing = true;
                this.form = {
                    id: item.id,
                    event: item.event,
                    layout_id: item.layout_id || 'padrao',
                    subject: item.subject,
                    internal_title: item.internal_title || '',
                    body_text: item.body_text,
                    is_active: item.is_active
                };
            } else {
                this.isEditing = false;
                this.form = { id: null, event: '', layout_id: 'padrao', subject: '', internal_title: '', body_text: '', is_active: true };
                this.previewData = { subject: '', html_preview: '' };
            }

            const firstTab = document.querySelector('#modalTemplate .nav-tabs button[data-bs-target="#tab-editor"]');
            if (firstTab) new window.bootstrap.Tab(firstTab).show();

            if (this.modalInstance) this.modalInstance.show();
        },

        async saveTemplate() {
            this.loading = true;
            try {
                if (this.isEditing) {
                    const response = await api.put(`/email-templates/${this.form.id}`, this.form);
                    const updated = response.data.template || response.data.data || response.data;
                    const index = this.templates.findIndex(c => c.id === this.form.id);
                    if (index !== -1) this.templates[index] = updated;
                    this.showAlert("Template atualizado com sucesso!");
                } else {
                    const response = await api.post('/email-templates', this.form);
                    const created = response.data.template || response.data.data || response.data;
                    this.templates.unshift(created);
                    this.showAlert("Template criado com sucesso!");
                }
                if (this.modalInstance) this.modalInstance.hide();
            } catch (e) {
                if (e.response?.status === 422) {
                    const errors = e.response.data?.errors || e.response.data?.data || {};
                    let msg = "Verifique os erros:\n";
                    for (const field in errors) msg += `- ${errors[field][0]}\n`;
                    this.showAlert(msg, "error");
                } else {
                    this.showAlert("Erro ao salvar o template.", "error");
                }
            } finally {
                this.loading = false;
            }
        },

        confirmDelete(target, isBulk = false) {
            this.itemToDelete = target;
            this.isBulkDelete = isBulk;
            new window.bootstrap.Modal(document.getElementById('confirmDeleteModal')).show();
        },

        async executeDeletion() {
            try {
                if (this.isBulkDelete) {
                    await api.post('/email-templates/bulk-delete', { ids: this.selectedIds });
                    this.templates = this.templates.filter(c => !this.selectedIds.includes(c.id));
                    this.totalItems -= this.selectedIds.length;
                    this.showAlert(`${this.selectedIds.length} templates excluídos.`);
                    this.selectedIds = [];
                } else {
                    await api.delete(`/email-templates/${this.itemToDelete.id}`);
                    this.templates = this.templates.filter(c => c.id !== this.itemToDelete.id);
                    this.totalItems -= 1;
                    this.showAlert(`Template removido.`);
                }
            } catch (error) {
                this.showAlert(error.response?.data?.message || 'Erro ao excluir.', 'error');
            } finally {
                const modalElement = document.getElementById('confirmDeleteModal');
                if (modalElement) window.bootstrap.Modal.getInstance(modalElement)?.hide();
            }
        },

        toggleAll(e) {
            this.selectedIds = e.target.checked ? this.templates.map(c => c.id) : [];
        }
    }
}
</script>

<style scoped>
.toast {
    min-width: 300px;
    border-radius: 12px;
    background-color: white !important;
}

.bg-success-subtle {
    background-color: #ecfdf5 !important;
    color: #059669 !important;
    border-left: 5px solid #059669 !important;
}

.bg-danger-subtle {
    background-color: #fef2f2 !important;
    color: #dc2626 !important;
    border-left: 5px solid #dc2626 !important;
}

.bg-error-subtle {
    background-color: #fef2f2 !important;
    color: #991b1b !important;
    border-left: 5px solid #ef4444 !important;
    animation: shake 0.4s ease-in-out;
}

.main-content {
    background-color: #f8fafc;
    min-height: 100vh;
    padding: 2rem;
    font-family: 'Inter', sans-serif;
}

.card {
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
}

.btn-pmg-primary {
    background: #561569;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-weight: 500;
}

.btn-pmg-primary:disabled {
    opacity: 0.5;
}

.btn-pmg-light {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    color: #64748b;
    padding: 10px 20px;
}

.btn-pmg-danger {
    background: #fee2e2;
    color: #b91c1c;
    border: 1px solid #fecaca;
    border-radius: 10px;
    font-weight: 600;
}

.btn-pmg-icon.border {
    border-color: #e2e8f0 !important;
    background: white;
    width: 38px;
    height: 38px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
}

.btn-pmg-icon:hover {
    background-color: #f1f5f9;
}

.form-control-pmg {
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 12px;
}

.form-control-pmg:focus {
    border-color: #561569;
    box-shadow: 0 0 0 4px rgba(33, 51, 97, 0.1);
}

.form-select.form-control-pmg {
    cursor: pointer;
}

.table-pmg thead th {
    background: #f8fafc;
    color: #64748b;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    padding: 1.2rem;
    border-bottom: 1px solid #edf2f7;
}

.table-pmg tbody td {
    padding: 1.2rem;
    border-bottom: 1px solid #f1f5f9;
}

.pmg-desc-cell {
    color: #64748b;
    font-size: 0.9rem;
    max-width: 500px;
    cursor: help;
}

.pmg-tabs {
    border-bottom: 2px solid #e2e8f0;
}

.pmg-tabs .nav-link {
    border: none;
    color: #64748b;
    padding: 12px 20px;
    border-bottom: 2px solid transparent;
    margin-bottom: -2px;
    transition: all 0.2s;
}

.pmg-tabs .nav-link:hover {
    color: #561569;
}

.pmg-tabs .nav-link.active {
    background: transparent;
    color: #561569;
    border-bottom: 2px solid #561569;
}

.layout-card:hover {
    border-color: #cbd5e1 !important;
    transform: translateY(-2px);
}

.layout-card.border-primary {
    border-color: #561569 !important;
}

.variable-pill {
    transition: transform 0.1s, border-color 0.2s, box-shadow 0.2s;
    border-radius: 8px;
    background: white;
}

.variable-pill:hover {
    transform: translateY(-2px);
    border-color: #561569 !important;
    box-shadow: 0 4px 8px rgba(33, 51, 97, 0.08);
    cursor: pointer;
}

.variable-pill:hover .text-dark {
    color: #561569 !important;
}

.variable-pill:hover .bi-cursor {
    opacity: 1 !important;
    color: #561569 !important;
}

.italic-text {
    font-style: italic;
    border-bottom: 1px dotted #ccc;
}

.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: all 0.4s ease;
}

.slide-fade-enter-from {
    transform: translateX(100px);
    opacity: 0;
}

.slide-fade-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-5px); }
    75% { transform: translateX(5px); }
}
</style>